import React, { useState, useEffect, useMemo } from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Slider } from '@/components/ui/slider';
import { getMetricsDataSource } from '../../services/MetricsService';

type AggregationLevel = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

interface TimeSliderProps {
  aggregation: AggregationLevel;
  onDateChange: (date: Date) => void;
}

function buildValidDates(aggregation: AggregationLevel, maxDaysBack: number, maxDaysForward: number): Date[] {
  const dates: Date[] = [];
  const makeDate = (year: number, month: number, day: number) =>
    new Date(year, month, day, 23, 59, 59, 999);

  const rangeStart = new Date();
  rangeStart.setDate(rangeStart.getDate() - maxDaysBack);
  const rangeEnd = new Date();
  rangeEnd.setDate(rangeEnd.getDate() + maxDaysForward);

  if (aggregation === 'daily') {
    for (let offset = -maxDaysBack; offset <= maxDaysForward; offset++) {
      const d = new Date();
      d.setDate(d.getDate() + offset);
      d.setHours(23, 59, 59, 999);
      dates.push(d);
    }
  } else if (aggregation === 'weekly') {
    // Find the Sunday on or before rangeStart
    const firstSunday = new Date(rangeStart);
    const dow = firstSunday.getDay(); // 0=Sun, 1=Mon, …
    const daysToLastSunday = dow; // 0 if already Sunday
    firstSunday.setDate(firstSunday.getDate() - daysToLastSunday);
    firstSunday.setHours(23, 59, 59, 999);
    const d = new Date(firstSunday);
    while (d <= rangeEnd) {
      dates.push(new Date(d));
      d.setDate(d.getDate() + 7);
    }
  } else if (aggregation === 'monthly') {
    const d = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
    while (d <= rangeEnd) {
      dates.push(makeDate(d.getFullYear(), d.getMonth(), 1));
      d.setMonth(d.getMonth() + 1);
    }
  } else if (aggregation === 'quarterly') {
    for (let year = rangeStart.getFullYear(); year <= rangeEnd.getFullYear() + 1; year++) {
      for (const month of [0, 3, 6, 9]) {
        const d = makeDate(year, month, 1);
        if (d >= rangeStart && d <= rangeEnd) dates.push(d);
      }
    }
  } else if (aggregation === 'yearly') {
    for (let year = rangeStart.getFullYear(); year <= rangeEnd.getFullYear(); year++) {
      dates.push(makeDate(year, 0, 1));
    }
  }

  return dates;
}

function findTodayIndex(dates: Date[]): number {
  const now = new Date();
  now.setHours(23, 59, 59, 999);
  let closest = 0;
  let minDiff = Infinity;
  dates.forEach((d, i) => {
    const diff = Math.abs(d.getTime() - now.getTime());
    if (diff < minDiff) { minDiff = diff; closest = i; }
  });
  return closest;
}

export function TimeSlider({ aggregation, onDateChange }: TimeSliderProps) {
  const isBlobSource = getMetricsDataSource() === 'blob';
  const maxDaysBack = 547;
  const maxDaysForward = isBlobSource ? 547 : 0;

  const validDates = useMemo(
    () => buildValidDates(aggregation, maxDaysBack, maxDaysForward),
    [aggregation, maxDaysBack, maxDaysForward]
  );

  const defaultIndex = useMemo(() => findTodayIndex(validDates), [validDates]);

  const [sliderIndex, setSliderIndex] = useState<number>(() => defaultIndex);

  // Reset to today when aggregation changes
  useEffect(() => {
    setSliderIndex(defaultIndex);
  }, [aggregation, defaultIndex]);

  // Notify parent whenever the selected date changes
  const selectedDate = useMemo(() => validDates[sliderIndex] ?? new Date(), [validDates, sliderIndex]);
  useEffect(() => {
    onDateChange(selectedDate);
  }, [selectedDate, onDateChange]);

  const isToday = sliderIndex === defaultIndex;

  return (
    <div className="flex items-center gap-3 ml-4 flex-1 max-w-[500px]">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setSliderIndex(defaultIndex)}
        disabled={isToday}
        className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
        title="Reset to current date"
      >
        <RotateCcw className="h-4 w-4" />
      </Button>
      <div className="flex-1">
        <Slider
          value={[sliderIndex]}
          min={0}
          max={validDates.length - 1}
          step={1}
          onValueChange={([v]: number[]) => setSliderIndex(v)}
        />
      </div>
      <span className="text-xs text-muted-foreground whitespace-nowrap min-w-[80px] text-right">
        {isToday
          ? 'Today'
          : selectedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
      </span>
    </div>
  );
}
